import React, { ReactNode, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import NotificationPollingService from "services/notificationPollingService";
import { fetchUserMenuPermissions } from "slices/menuPermissions";
import PostLoginLoading from "components/LoadingStates/PostLoginLoading";

let appBootPromise: Promise<void> | null = null;
let appBootCompleted = false;
const MIN_POST_LOGIN_LOADER_MS = 1200;

interface AuthProtectedProps {
  children: ReactNode;
}

const AuthProtected: React.FC<AuthProtectedProps> = ({ children }) => {
  const dispatch = useDispatch();
  const [phase, setPhase] = useState<"loading" | "finishing" | "done">(
    appBootCompleted ? "done" : "loading"
  );
  const loaderStartRef = React.useRef<number>(Date.now());

  useEffect(() => {
    const initializeServices = async () => {
      try {
        // Initialize notification service
        const notificationService = NotificationPollingService.getInstance();
        if (notificationService.isNotificationSupported()) {
          await notificationService.requestNotificationPermission();
          await notificationService.startPolling();
        }

        // Check if menu needs refresh
        const menuNeedsRefresh = localStorage.getItem('menuNeedsRefresh');
        const shouldCacheBust = menuNeedsRefresh === 'true';
        
        // Load user menu permissions
        await dispatch(fetchUserMenuPermissions(shouldCacheBust) as any);
        
        // Clear the flag if it was set
        if (shouldCacheBust) {
            localStorage.removeItem('menuNeedsRefresh');
        }
      } catch (error) {
        console.error('Error initializing services:', error);
      }
    };

    // Initialize services when component mounts
    // Only block UI for the first boot after login/refresh.
    const forceShowLoader = sessionStorage.getItem("postLoginLoader") === "1";
    if (forceShowLoader) {
      loaderStartRef.current = Date.now();
      setPhase("loading");
      // Clear immediately so it only applies once.
      sessionStorage.removeItem("postLoginLoader");
    }

    if (!appBootCompleted) {
      if (!appBootPromise) {
        appBootPromise = initializeServices().finally(() => {
          appBootCompleted = true;
        });
      }

      appBootPromise.finally(() => {
        const elapsed = Date.now() - loaderStartRef.current;
        const remaining = Math.max(0, MIN_POST_LOGIN_LOADER_MS - elapsed);
        window.setTimeout(() => setPhase("finishing"), remaining);
      });
    } else if (forceShowLoader) {
      // Boot already completed, but we still want the loader to show briefly after login.
      window.setTimeout(() => setPhase("finishing"), MIN_POST_LOGIN_LOADER_MS);
    }
  }, [dispatch]);

  // Check for both auth token and user details
  const authUser = localStorage.getItem("authUser");
  const userDetails = localStorage.getItem("userDetails");
  
  if (!authUser || !userDetails) {
    // Reset boot state so next login shows the loader again.
    appBootPromise = null;
    appBootCompleted = false;
    sessionStorage.removeItem("postLoginLoader");
    return <Navigate to={{ pathname: "/login" }} />;
  }

  // Validate that authUser is a valid JSON and not empty
  try {
    const token = JSON.parse(authUser);
    if (!token || token === "") {
      localStorage.removeItem("authUser");
      localStorage.removeItem("authUserRefreshToken");
      localStorage.removeItem("userDetails");
      appBootPromise = null;
      appBootCompleted = false;
      sessionStorage.removeItem("postLoginLoader");
      return <Navigate to={{ pathname: "/login" }} />;
    }
  } catch (error) {
    localStorage.removeItem("authUser");
    localStorage.removeItem("authUserRefreshToken");
    localStorage.removeItem("userDetails");
    appBootPromise = null;
    appBootCompleted = false;
    sessionStorage.removeItem("postLoginLoader");
    return <Navigate to={{ pathname: "/login" }} />;
  }

  if (phase !== "done") {
    return (
      <PostLoginLoading
        companyName="Company"
        readyToComplete={phase === "finishing"}
        onComplete={() => setPhase("done")}
      />
    );
  }

  return <React.Fragment>{children}</React.Fragment>;
};

export default AuthProtected;
